import { all, call, put, takeEvery, takeLatest } from 'redux-saga/effects';
import qs from 'qs';
import { request } from '../../common/util/RestClient';
import envConfig from '../../config';
import {
  createSurvey,
  deleteSurvey,
  deleteSurveyResponse,
  deactivateSurvey,
  editSurvey,
  editSurveyStatus,
  fetchSurveyById,
  fetchSurveyEmailDomain,
  fetchSurveyResponses,
  fetchSurveys,
  fetchSurveyStatBarData,
  publishSurvey,
  sendSurveyEmail,
  verifySurveyInstallation,
  fetchSurveyResponsesByEntity,
  bookmarkSurvey,
} from '../routines/surveyRoutines';
import SUCCESS_MESSAGES from '../../common/constants/SuccessMessagesConstants';
import { convertFilterToVfl } from '../../common/util/velarisFilterLanguageUtils';
import { BOOLEAN_INT_MAPPING } from '../../common/constants/CommonConstants';

const SURVEY_MANAGEMENT_BASE_URL = `${envConfig.APIG_URL}survey-management`;

function getSurveyResponsesApiHelper(payload = {}, overRideURL = null) {
  const { surveyId, startAtKey, limit = 100, filters } = payload;
  const params = {
    startAtKey,
    limit,
    ...(filters && filters.length > 0
      ? { filters: convertFilterToVfl(filters) }
      : {}),
  };
  const baseURL =
    overRideURL ||
    `${SURVEY_MANAGEMENT_BASE_URL}/surveys/${surveyId}/responses`;
  const url = `${baseURL}?${qs.stringify(params)}`;
  return request(url);
}

function* fetchSurveysSaga() {
  try {
    const url = `${SURVEY_MANAGEMENT_BASE_URL}/surveys`;
    const response = yield call(request, url);
    yield put(fetchSurveys.success(response.data));
  } catch (error) {
    yield put(fetchSurveys.failure({ error }));
  } finally {
    yield put(fetchSurveys.fulfill());
  }
}

function* fetchSurveyByIdSaga({ payload }) {
  try {
    const { surveyId } = payload;
    const url = `${SURVEY_MANAGEMENT_BASE_URL}/surveys/${surveyId}?`;
    const response = yield call(request, url);
    yield put(fetchSurveyById.success(response.data));
  } catch (error) {
    yield put(fetchSurveyById.failure({ error }));
  }
}

function* createSurveySaga({ payload }) {
  try {
    const url = `${SURVEY_MANAGEMENT_BASE_URL}/surveys`;
    const config = {
      method: 'POST',
      payload,
    };
    const { data } = yield call(request, url, config);
    yield put(fetchSurveyById.trigger({ surveyId: data.surveyId }));
    yield put(
      createSurvey.success({
        successMessage: SUCCESS_MESSAGES.CREATE_SURVEY,
      })
    );

    yield put(fetchSurveys.trigger({}));
  } catch (error) {
    yield put(createSurvey.failure({ error }));
  } finally {
    yield put(createSurvey.fulfill());
  }
}

export function* publishSurveySaga({ payload }) {
  try {
    const { surveyId } = payload;
    const url = `${SURVEY_MANAGEMENT_BASE_URL}/surveys/${surveyId}/publish`;
    const config = {
      method: 'POST',
      payload: {},
    };
    yield call(request, url, config);
    yield put(
      publishSurvey.success({
        successMessage: SUCCESS_MESSAGES.PUBLISH_SURVEY,
      })
    );

    yield put(fetchSurveyById.trigger({ surveyId }));
    yield put(fetchSurveys.trigger({}));
  } catch (error) {
    yield put(publishSurvey.failure({ error }));
  } finally {
    yield put(publishSurvey.fulfill());
  }
}

export function* editSurveySaga({ payload }) {
  try {
    const {
      surveyId,
      showAlert = true,
      updateList = true,
      isPublish = false,
      ...rest
    } = payload;
    const url = `${SURVEY_MANAGEMENT_BASE_URL}/surveys/${surveyId}`;
    const config = {
      method: 'PUT',
      payload: { ...rest },
    };
    yield call(request, url, config);

    if (isPublish) {
      yield call(publishSurveySaga, { payload: { surveyId } });
    } else {
      yield put(
        editSurvey.success({
          ...(showAlert && { successMessage: SUCCESS_MESSAGES.EDIT_SURVEY }),
        })
      );
    }

    if (updateList && !isPublish) {
      yield put(fetchSurveyById.trigger({ surveyId }));
      yield put(fetchSurveys.trigger({}));
    }
  } catch (error) {
    yield put(editSurvey.failure({ error }));
  } finally {
    yield put(editSurvey.fulfill());
  }
}

function* deleteSurveySaga({ payload }) {
  try {
    const { surveyId } = payload;
    const url = `${SURVEY_MANAGEMENT_BASE_URL}/surveys/${surveyId}`;
    const config = {
      method: 'DELETE',
    };
    yield call(request, url, config);
    yield put(
      deleteSurvey.success({
        successMessage: SUCCESS_MESSAGES.DELETE_SURVEY,
      })
    );

    yield put(fetchSurveys.trigger({}));
  } catch (error) {
    yield put(deleteSurvey.failure({ error }));
  } finally {
    yield put(deleteSurvey.fulfill());
  }
}

function* deleteSurveyResponseSaga({ payload }) {
  try {
    const { id, surveyId, filters = [] } = payload;
    const url = `${SURVEY_MANAGEMENT_BASE_URL}/surveys/${surveyId}/responses/${id}`;
    const config = {
      method: 'DELETE',
    };
    yield call(request, url, config);
    yield put(
      deleteSurveyResponse.success({
        successMessage: SUCCESS_MESSAGES.DELETE_SURVEY_RESPONSE,
      })
    );

    yield put(
      fetchSurveyResponses.trigger({
        preservePreviousList: false,
        surveyId,
        filters,
      })
    );
  } catch (error) {
    yield put(deleteSurveyResponse.failure({ error }));
  } finally {
    yield put(deleteSurveyResponse.fulfill());
  }
}

function* editSurveyStatusSaga({ payload }) {
  try {
    const { surveyId, paused } = payload;
    const url = `${SURVEY_MANAGEMENT_BASE_URL}/surveys/${surveyId}/status`;
    const config = {
      method: 'POST',
      payload: {
        paused,
      },
    };
    yield call(request, url, config);
    yield put(
      editSurveyStatus.success({
        successMessage:
          Number(paused) === BOOLEAN_INT_MAPPING.TRUE
            ? SUCCESS_MESSAGES.PAUSE_SURVEY
            : SUCCESS_MESSAGES.RESUME_SURVEY,
      })
    );

    yield put(fetchSurveys.trigger({}));
  } catch (error) {
    yield put(editSurveyStatus.failure({ error }));
  } finally {
    yield put(editSurveyStatus.fulfill());
  }
}

/**
 * Saga function responsible for deactivating a survey.
 */
export function* deactivateSurveySaga({ payload }) {
  try {
    const { surveyId } = payload;
    const url = `${SURVEY_MANAGEMENT_BASE_URL}/surveys/deactivate/${surveyId}`;
    const config = {
      method: 'POST',
    };
    yield call(request, url, config);
    yield put(
      deactivateSurvey.success({
        successMessage: SUCCESS_MESSAGES.DEACTIVATE_SURVEY,
      })
    );

    yield put(fetchSurveys.trigger({}));
  } catch (error) {
    yield put(deactivateSurvey.failure({ error }));
  } finally {
    yield put(deactivateSurvey.fulfill());
  }
}

function* sendSurveyEmailSaga({ payload }) {
  try {
    const { surveyId } = payload;
    const url = `${SURVEY_MANAGEMENT_BASE_URL}/surveys/${surveyId}/send-email`;
    const config = {
      method: 'POST',
      payload: {},
    };
    yield call(editSurveySaga, {
      payload: { ...payload, showAlert: false, updateList: false },
    });
    yield call(request, url, config);
    yield put(
      sendSurveyEmail.success({
        successMessage: SUCCESS_MESSAGES.SEND_SURVEY_EMAIL,
      })
    );

    yield put(fetchSurveys.trigger({}));
  } catch (error) {
    yield put(sendSurveyEmail.failure({ error }));
  } finally {
    yield put(sendSurveyEmail.fulfill());
  }
}

function* verifySurveyInstallationSaga({ payload }) {
  try {
    const { surveyId } = payload;
    const url = `${SURVEY_MANAGEMENT_BASE_URL}/surveys/${surveyId}/verify-installation`;
    const config = {
      method: 'POST',
      payload: {},
    };
    yield call(request, url, config);
    yield put(verifySurveyInstallation.success({}));

    yield put(fetchSurveyById.trigger({ surveyId }));
  } catch (error) {
    yield put(verifySurveyInstallation.failure({ error }));
  } finally {
    yield put(verifySurveyInstallation.fulfill());
  }
}

export function* fetchSurveyResponsesSaga({ payload }) {
  try {
    const response = yield call(getSurveyResponsesApiHelper, payload);
    yield put(
      fetchSurveyResponses.success({
        ...response.data,
        preservePreviousList: payload.preservePreviousList,
      })
    );
  } catch (error) {
    yield put(fetchSurveyResponses.failure({ error }));
  } finally {
    yield put(fetchSurveyResponses.fulfill());
  }
}

export function* fetchSurveyResponsesByEntitySaga({ payload }) {
  try {
    const { level, entityId, lastFetchedNPSKey, lastFetchedCSATKey, filters } =
      payload;
    let url = `${SURVEY_MANAGEMENT_BASE_URL}/surveys/responses/${level}/entity/${entityId}`;
    const params = {
      lastFetchedCSATKey,
      lastFetchedNPSKey,
      limit: 200,
      ...(filters && filters.length > 0
        ? { filters: convertFilterToVfl(filters) }
        : {}),
    };

    url = `${url}?${qs.stringify(params)}`;

    const response = yield call(request, url);

    yield put(
      fetchSurveyResponsesByEntity.success({
        ...response.data,
        preservePreviousList: payload.preservePreviousList,
      })
    );
  } catch (error) {
    yield put(fetchSurveyResponsesByEntity.failure({ error }));
  } finally {
    yield put(fetchSurveyResponsesByEntity.fulfill());
  }
}
export function* bookmarkSurveySaga({ payload }) {
  try {
    const { level, entityId, id, status, version, type } = payload;
    const URL = `${SURVEY_MANAGEMENT_BASE_URL}/surveys/bookmark/${type}/${id}?status=${status}&version=${version}`;
    const config = {
      method: 'PATCH',
      payload: {},
    };
    const response = yield call(request, URL, config);
    yield put(bookmarkSurvey.success(response));
    yield put(fetchSurveyResponsesByEntity.trigger({ level, entityId }));
  } catch (err) {
    yield put(bookmarkSurvey.failure({ error: err }));
  }
}

function* fetchSurveyStatBarDataSaga({ payload }) {
  try {
    const { surveyId } = payload;
    const url = `${SURVEY_MANAGEMENT_BASE_URL}/surveys/${surveyId}/stat-bar`;
    const response = yield call(request, url);
    yield put(fetchSurveyStatBarData.success(response.data));
  } catch (error) {
    yield put(fetchSurveyStatBarData.failure({ error }));
  }
}
function* fetchSurveyEmailDomainSaga() {
  try {
    const url = `${SURVEY_MANAGEMENT_BASE_URL}/config/email-domain`;
    const response = yield call(request, url);
    yield put(fetchSurveyEmailDomain.success(response.data?.value));
  } catch (error) {
    yield put(fetchSurveyEmailDomain.failure({ error }));
  }
}

export default function* root() {
  yield all([
    takeLatest(fetchSurveys.TRIGGER, fetchSurveysSaga),
    takeLatest(fetchSurveyById.TRIGGER, fetchSurveyByIdSaga),
    takeLatest(fetchSurveyResponses.TRIGGER, fetchSurveyResponsesSaga),
    takeLatest(fetchSurveyStatBarData.TRIGGER, fetchSurveyStatBarDataSaga),
    takeLatest(fetchSurveyEmailDomain.TRIGGER, fetchSurveyEmailDomainSaga),
    takeLatest(
      fetchSurveyResponsesByEntity.TRIGGER,
      fetchSurveyResponsesByEntitySaga
    ),
    takeEvery(bookmarkSurvey.TRIGGER, bookmarkSurveySaga),
    takeEvery(publishSurvey.TRIGGER, publishSurveySaga),
    takeEvery(sendSurveyEmail.TRIGGER, sendSurveyEmailSaga),
    takeEvery(verifySurveyInstallation.TRIGGER, verifySurveyInstallationSaga),
    takeEvery(editSurveyStatus.TRIGGER, editSurveyStatusSaga),
    takeEvery(deactivateSurvey.TRIGGER, deactivateSurveySaga),
    takeEvery(editSurvey.TRIGGER, editSurveySaga),
    takeEvery(createSurvey.TRIGGER, createSurveySaga),
    takeEvery(deleteSurvey.TRIGGER, deleteSurveySaga),
    takeEvery(deleteSurveyResponse.TRIGGER, deleteSurveyResponseSaga),
  ]);
}
